//
/* GS://PROTOCOLO
 * Arquivo: src/components/pos/PointsCard.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.6.0 – PARIDADE LÓGICA (Trava 50% & Múltiplos de 5)
 * Impacto da Versão: [src/components/pos/PointsCard.js]
 * Data: 19/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [GS-LOGIC]: Implementada trava de 50% do subtotal (conforme App Cliente).
 * - [GS-LOGIC]: Valores forçados para múltiplos de 5 (arredondamento para baixo).
 * - [GS-UI]: Feedback visual imediato do limite máximo permitido.
 * - [GS-FIX]: Correção de recálculo automático quando o carrinho muda.
 */
//

import React, { useState, useEffect } from 'react';
import { Star, AlertCircle } from 'lucide-react';

const PointsCard = ({ customer, subtotal, onDiscountUpdate }) => {
    // GS-CORRECAO-INICIO: Estados para controle de input e mensagens
    const [discountInputDisplay, setDiscountInputDisplay] = useState('');
    const [currentAppliedPoints, setCurrentAppliedPoints] = useState(0);
    const [limitInfo, setLimitInfo] = useState({ maxAllowed: 0, reason: '' });
    // GS-CORRECAO-FIM

    // GS-CORRECAO-INICIO: Lógica Central de Cálculo de Limites (Paridade com App Flutter)
    useEffect(() => {
        if (!customer) return;

        const availablePoints = customer.availablePoints || 0;
        
        // Regra 1: Teto financeiro de 50% do valor da compra
        const financialCap = subtotal * 0.5;

        // Regra 2: O limite é o menor valor entre o Saldo e o Teto Financeiro
        const absoluteLimit = Math.min(availablePoints, financialCap);

        // Regra 3: Múltiplos de 5 (Arredondamento para baixo para garantir integridade)
        // Ex: Se limite é 98, vira 95. Se é 12, vira 10.
        const validLimit = Math.floor(absoluteLimit / 5) * 5;

        setLimitInfo({
            maxAllowed: validLimit,
            reason: validLimit < availablePoints ? 'Limitado a 50% da compra' : 'Saldo disponível'
        });

        // Se o valor já aplicado exceder o novo limite (ex: removeu itens do carrinho), ajusta automaticamente
        if (currentAppliedPoints > validLimit) {
            applyPoints(validLimit);
        } else if (subtotal <= 0) {
            // Se carrinho zerou, limpa tudo
            applyPoints(0);
        }

    }, [customer, subtotal, currentAppliedPoints]);
    // GS-CORRECAO-FIM

    // Função para aplicar os pontos e notificar o componente pai
    const applyPoints = (points) => {
        // Garante que é múltiplo de 5 e positivo
        const safePoints = Math.max(0, Math.floor(points / 5) * 5);
        
        setCurrentAppliedPoints(safePoints);
        setDiscountInputDisplay(safePoints > 0 ? safePoints.toString() : '');

        // Notifica o pai (POSPage). Assumindo paridade 1 GP = R$ 1,00
        if (onDiscountUpdate) {
            onDiscountUpdate({
                discount: safePoints, // Valor em R$
                cost: safePoints      // Custo em Pontos
            });
        }
    };

    const handleBlur = () => {
        let value = parseInt(discountInputDisplay.replace(/\D/g, ''), 10) || 0;

        // Validação Rígida ao sair do campo
        if (value > limitInfo.maxAllowed) {
            value = limitInfo.maxAllowed;
        }

        // Força múltiplo de 5
        value = Math.floor(value / 5) * 5;

        applyPoints(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur(); // Dispara o handleBlur
        }
    };

    const handleChange = (e) => {
        // Permite digitar apenas números para UX fluida, validação ocorre no Blur/Enter
        const raw = e.target.value.replace(/\D/g, '');
        setDiscountInputDisplay(raw);
    };

    if (!customer) return null;

    const displayPlanName = (customer?.activeSubscription?.planName || customer?.subscriptionPlan || 'Aspirante').trim();

    return (
        <div className="relative p-4 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="text-lg font-bold text-brand-gold flex items-center gap-2">
                <Star size={18} /> Usar Golden Points
            </h3>
            
            <div className="text-sm text-gray-300 mt-2 flex justify-between items-end">
                <div>
                    <p>Saldo: <span className="font-bold text-white">{customer.availablePoints || 0}</span> GP</p>
                    <p className="text-xs text-gray-500">{displayPlanName}</p>
                </div>
                {/* GS-CORRECAO-INICIO: Exibição do limite calculado */}
                <div className="text-right">
                    <p className="text-xs text-gray-400">Máximo permitido nesta compra:</p>
                    <p className="font-bold text-brand-gold">{limitInfo.maxAllowed} GP</p>
                </div>
                {/* GS-CORRECAO-FIM */}
            </div>

            <div className="mt-3">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                    Pontos a utilizar (Múltiplos de 5)
                </label>
                <div className="relative">
                    <input
                        type="text"
                        value={discountInputDisplay}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        placeholder={`Máx: ${limitInfo.maxAllowed}`}
                        className={`w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:outline-none focus:ring-brand-gold ${currentAppliedPoints >= limitInfo.maxAllowed && limitInfo.maxAllowed > 0 ? 'border-brand-gold' : ''}`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-bold">
                        GP
                    </div>
                </div>

                {/* GS-CORRECAO-INICIO: Mensagens de feedback */}
                {limitInfo.maxAllowed === 0 && subtotal > 0 && (customer.availablePoints || 0) > 0 && (
                    <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Valor da compra muito baixo para usar pontos (Mín. 50%).
                    </p>
                )}

                {currentAppliedPoints > 0 && (
                    <p className="text-xs text-center text-green-400 mt-2">
                        Desconto aplicado: R$ {currentAppliedPoints.toFixed(2)}
                    </p>
                )}
                
                <p className="text-[10px] text-gray-500 mt-2 text-center">
                    Regra: Máximo de 50% do valor da compra em pontos.
                </p>
                {/* GS-CORRECAO-FIM */}
            </div>
        </div>
    );
};

export default PointsCard;